import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Container, CssBaseline, Typography, TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { Link as MUILink } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/features/redux-hooks";
import PhoneInput from "react-phone-input-2";
// @ts-ignore: imported for side effects, no type declarations for CSS module
import "react-phone-input-2/lib/material.css";
import { login } from "../../../redux/features/Auth/AuthThunk";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "../../../components/common/SnackBar/snackBar";
import loginPageStyles from "../../../styles/PublicPage/login";
import { PATH_NAME } from "../../../configs/pathName";
import { syncUserToShopify } from "../../../api/AuthAPI";
import { decodeMachineLoginPayload, syncMachineLoginToShakeHQ } from "../../../helpers/QrcodeMachineLogin/qrcodeMachineLogin";
// import { useCart } from "../../../context/CartContext";
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import assets from "../../../assets";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  // const { mergeGuestCartWithUserCart } = useCart();
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const machineLinkHandledRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const basicUserInfo = useAppSelector(
    (state) => state.authState.basicUserInfo
  );
  const shouldSyncShopifyOnMountRef = useRef(Boolean(basicUserInfo));

  const urlParams = new URLSearchParams(window.location.search);
  const sessionRef = urlParams.get("session_ref");
  const machineId = urlParams.get("machine_id");
  const qValue = urlParams.get("q");
  const decodedMachinePayload = decodeMachineLoginPayload(qValue);
  const decodedMachineId = decodedMachinePayload.machine_id;
  const decodedMachineSession = decodedMachinePayload.machine_session;

  const syncMachineLogin = useCallback(async () => {
    if (!decodedMachineId || !decodedMachineSession || machineLinkHandledRef.current) {
      return false;
    }

    machineLinkHandledRef.current = true;

    try {
      const result = await syncMachineLoginToShakeHQ({
        machineId: decodedMachineId,
        machineSession: decodedMachineSession,
      });

      if (result) {
        toast.success("Successfully logged in vending machine");
      }

      return result;
    } catch (error: any) {
      toast.error(error.message || "Failed to link vending machine.");
      return false;
    }
  }, [decodedMachineId, decodedMachineSession]);

  useEffect(() => {
    if (!basicUserInfo || isRedirecting || machineLinkHandledRef.current) {
      return;
    }

    const runMachineLoginFlow = async () => {
      if (shouldSyncShopifyOnMountRef.current) {
        shouldSyncShopifyOnMountRef.current = false;
        try {
          await syncUserToShopify();
        } catch (error) {
          console.error("Failed to sync existing user to Shopify from login page.", error);
        }
      }

      await syncMachineLogin();
      setIsRedirecting(true);
    };

    runMachineLoginFlow();
  }, [basicUserInfo, isRedirecting, syncMachineLogin]);

  if (sessionRef && machineId) {
    const sessionTime = Date.now();
    localStorage.setItem("session_ref", sessionRef);
    localStorage.setItem("session_time", sessionTime.toString());
    localStorage.setItem("machine_id", machineId);
  }

  if (decodedMachineId) {
    localStorage.setItem("machine_id", decodedMachineId.toString());
  }

  if (decodedMachineSession) {
    localStorage.setItem("machine_session", decodedMachineSession);
  }

  if (basicUserInfo) {
    if (!isRedirecting) {
      return null;
    }

    return <Navigate replace to={`${PATH_NAME.MEMBER}${window.location.search}`} />;
  }

  const handlePhoneChange = (value: string, data: any) => {
    const phoneNumber = value.slice(data.dialCode.length);
    setPhone(phoneNumber);
    setCountryCode(`+${data.dialCode}`);
  };

  const handleLogin = async () => {
    const phoneNumber = phone.replace(/[^0-9]/g, "");

    if (phone && password) {
      try {
        await dispatch(
          login({
            country_code: countryCode,
            mobile: phoneNumber,
            password,
          })
        ).unwrap();

        if (decodedMachineId && decodedMachineSession) {
          const machineLoginHandled = await syncMachineLogin();
          if (!machineLoginHandled) {
            toast.success("Login successfully!");
          }
        } else {
          toast.success("Login successfully!");
        }
      } catch {
        // No action needed; errors handled at API level
      }
    } else {
      toast.error("Please enter Phone Number & Password.");
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Box sx={loginPageStyles.container}>
        <Container maxWidth="xs" sx={loginPageStyles.contentContainer}>
          <CssBaseline />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            sx={{
              ...typo.sectionHeadline,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colorConfigs.primary,
                },
                "&:hover fieldset": {
                  borderColor: colorConfigs.primary,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colorConfigs.primary,
                },
              },
            }}
          >
            Login
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                ...typo.bodyContent,
                ...loginPageStyles.phoneInputContainer,
              }}
            >
              <PhoneInput
                country={"sg"}
                value={countryCode + phone}
                onChange={handlePhoneChange}
                inputProps={{
                  name: "phone",
                  required: true,
                  autoFocus: true,
                }}
                containerStyle={{
                  width: "100%",
                  border: "1px solid",
                  borderRadius: "4px",
                  borderColor: colorConfigs.primary,
                  ...typo.bodyContent,

                }}
                inputStyle={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  ...typo.bodyContent,
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    input: {
                      ...typo.bodyContent,
                    },
                  },
                  inputProps: {
                    autoCapitalize: "none",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    ...typo.bodyContent,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      ...typo.bodyContent,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colorConfigs.primary,
                      ...typo.bodyContent,
                    },
                  },
                  "& .MuiInputLabel-root": {},
                  "& .MuiInputLabel-root.Mui-focused": {
                    ...typo.bodyContent,
                  },
                }}
              />

              <Box sx={loginPageStyles.formActions}>
                <Typography variant="body2">
                  <RouterLink
                    to={PATH_NAME.FORGET_PASSWORD}
                    style={{
                      ...typo.bodyContent,
                      ...loginPageStyles.forgotPasswordLink,
                    }}
                  >
                    Forgot password?
                  </RouterLink>
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                sx={{ ...typo.button, ...loginPageStyles.loginButton }}
                onClick={handleLogin}
              >
                Login
              </Button>
              <Typography
                variant="body2"
                align="center"
                sx={{ ...typo.button }}
              >
                Don’t have an account yet?{" "}
                <RouterLink to={"/register"} style={loginPageStyles.signupLink}>
                  Sign up
                </RouterLink>
              </Typography>
            </Box>
          </Box>
          {isMobile && (
            <Box
              sx={{
                padding: "12px 16px",
                marginBottom: "24px",
                marginTop: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                position: "relative",
              }}
            >
              <Typography
                variant="body2"
                sx={{ ...typo.bodyContent, color: colorConfigs.tertiary, marginBottom: "8px", lineHeight: "1.25" }}
              >
                Enjoy faster access and smoother navigation with our mobile app!
              </Typography>

              <Box sx={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                <MUILink
                  href="https://play.google.com/store/apps/details?id=com.nommi.club"
                  target="_blank"
                  rel="noopener"
                >
                  <img
                    src={assets.images.GooglePlayStore}
                    alt="Download on Play Store"
                    width={150}
                    height={50}
                  />
                </MUILink>
                <MUILink
                  href="https://apps.apple.com/my/app/nommi-club/id6744875110"
                  target="_blank"
                  rel="noopener"
                >
                  <img
                    src={assets.images.AppStore}
                    alt="Download on App Store"
                    width={150}
                    height={50}
                  />
                </MUILink>
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Login;
